import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Plus, Pencil, Trash2, MapPin, Save, Loader2, RefreshCcw, Power, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Field, Modal } from "./AdminRestaurants";
import { LocationSearchBar } from "../../components/AddressPickerModal";
import PermissionGateModal from "../../components/PermissionGateModal";
import { getCurrentLocation, requestLocationPermission } from "../../lib/geolocation";

// Fix default marker icons (Leaflet quirk in CRA)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [17.385, 78.4867]; // Hyderabad fallback

function ClickPicker({ onPick }) {
    useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
    return null;
}

function Recenter({ to }) {
    const map = useMap();
    useEffect(() => { if (to) map.setView(to, map.getZoom()); }, [to, map]);
    return null;
}

export default function AdminZones() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState({
        name: "", center_lat: DEFAULT_CENTER[0], center_lng: DEFAULT_CENTER[1],
        radius_km: 5, base_delivery_fee: 25, per_km_fee: 10, min_order_value: 99, pincodes: "",
        free_delivery_enabled: false, free_delivery_above: 499,
    });
    const [busy, setBusy] = useState(false);
    const [provider, setProvider] = useState(() => localStorage.getItem("bizil_map_provider") || "osm");
    const [gKey, setGKey] = useState(() => localStorage.getItem("bizil_google_maps_key") || "");
    const [tenantSettings, setTenantSettings] = useState({ zone_offline_message: "" });
    // iter102c — wedge-safe location gate for the zone editor's "Use current location" button.
    const [locGate, setLocGate] = useState(null);
    const applyCoords = (lat, lng) => setForm((f) => ({
        ...f, center_lat: Number(lat).toFixed(6), center_lng: Number(lng).toFixed(6),
    }));
    const useZoneCurrentLocation = async () => {
        // See CustomerHeader / ShopMapPicker — migrate off raw
        // ``navigator.geolocation`` so we get the watchdog + wedge-safe
        // path from the shared helper.
        const r = await getCurrentLocation();
        if (r.state === "granted" && r.coords) {
            applyCoords(r.coords.lat, r.coords.lng);
            return;
        }
        if (r.state === "unsupported") return toast.error("Geolocation not supported");
        if (r.state === "denied") return setLocGate("denied");
        if (r.state === "prompt") return setLocGate("prompt");
        toast.error(r.error || "Couldn't fetch location");
    };

    const load = () => api.get("/admin/zones").then(({ data }) => setRows(data.filter(z => z.status !== "DELETED")));
    useEffect(() => {
        load();
        api.get("/admin/tenant-settings").then(({ data }) => setTenantSettings({ zone_offline_message: data.zone_offline_message || "" }));
    }, []);

    const saveOfflineMsg = async () => {
        await api.patch("/admin/tenant-settings", { zone_offline_message: tenantSettings.zone_offline_message });
        toast.success("Offline banner message saved");
    };

    const toggleZone = async (z) => {
        const next = z.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        await api.patch(`/admin/zones/${z.id}`, { status: next });
        toast.success(`${z.name} ${next === "ACTIVE" ? "turned ON" : "turned OFF"}`);
        load();
    };

    const openCreate = () => {
        setEdit(null);
        setForm({ name: "", center_lat: DEFAULT_CENTER[0], center_lng: DEFAULT_CENTER[1], radius_km: 5, base_delivery_fee: 25, per_km_fee: 10, min_order_value: 99, pincodes: "", free_delivery_enabled: false, free_delivery_above: 499, gst_on_delivery_enabled: null, gst_on_delivery_percent: 5, cod_enabled: null, online_payment_enabled: null });
        setOpen(true);
    };
    const openEdit = (z) => {
        setEdit(z);
        setForm({
            name: z.name,
            center_lat: z.center_lat ?? DEFAULT_CENTER[0],
            center_lng: z.center_lng ?? DEFAULT_CENTER[1],
            radius_km: z.radius_km, base_delivery_fee: z.base_delivery_fee,
            per_km_fee: z.per_km_fee, min_order_value: z.min_order_value || 99,
            pincodes: (z.pincodes || []).join(", "),
            free_delivery_enabled: !!z.free_delivery_enabled,
            free_delivery_above: z.free_delivery_above ?? 499,
            gst_on_delivery_enabled: z.gst_on_delivery_enabled,
            gst_on_delivery_percent: z.gst_on_delivery_percent ?? 5,
            cod_enabled: z.cod_enabled,
            online_payment_enabled: z.online_payment_enabled,
        });
        setOpen(true);
    };

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                name: form.name,
                center_lat: Number(form.center_lat),
                center_lng: Number(form.center_lng),
                radius_km: Number(form.radius_km),
                base_delivery_fee: Number(form.base_delivery_fee),
                per_km_fee: Number(form.per_km_fee),
                min_order_value: Number(form.min_order_value),
                pincodes: form.pincodes.split(",").map((s) => s.trim()).filter(Boolean),
                free_delivery_enabled: !!form.free_delivery_enabled,
                free_delivery_above: form.free_delivery_enabled && form.free_delivery_above !== "" ? Number(form.free_delivery_above) : null,
                gst_on_delivery_enabled: form.gst_on_delivery_enabled,
                gst_on_delivery_percent: form.gst_on_delivery_enabled === null ? null : Number(form.gst_on_delivery_percent),
                cod_enabled: form.cod_enabled,
                online_payment_enabled: form.online_payment_enabled,
            };
            if (edit) {
                await api.patch(`/admin/zones/${edit.id}`, payload);
                toast.success("Zone updated. Restaurants re-assigned.");
            } else {
                await api.post(`/admin/zones`, { tenant_id: "x", status: "ACTIVE", ...payload });
                toast.success("Zone created. Restaurants assigned by location.");
            }
            setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this zone? Past orders inside it will keep their reference.")) return;
        try {
            const { data } = await api.delete(`/admin/zones/${id}`);
            toast.success(data?.mode === "archived" ? `Archived — ${data.orders} past order(s) kept` : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const reassignAll = async () => {
        const { data } = await api.post("/admin/zones/reassign-all");
        toast.success(`Reassigned ${data.assigned} restaurants by location`);
    };

    const setProviderAndKey = () => {
        localStorage.setItem("bizil_map_provider", provider);
        localStorage.setItem("bizil_google_maps_key", gKey);
        toast.success("Map provider saved");
    };

    const tileUrl = provider === "google" && gKey
        ? `https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=${gKey}`
        : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    const tileAttr = provider === "google" ? "© Google" : "© OpenStreetMap";

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Zones & Delivery Pricing</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} zones • Click on the map to set centre • Adjust radius to define serviceable area.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={reassignAll} className="bg-muted rounded-xl px-3 py-2 font-semibold text-sm inline-flex items-center gap-2"><RefreshCcw size={14} /> Reassign restaurants</button>
                    <button data-testid="add-zone" onClick={openCreate} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> New zone</button>
                </div>
            </div>

            <div className="mt-4 bg-card border border-border rounded-2xl p-4">
                <p className="text-sm font-semibold">Map provider</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <select value={provider} onChange={(e) => setProvider(e.target.value)} className="inp max-w-[200px]">
                        <option value="osm">OpenStreetMap (free)</option>
                        <option value="google">Google Maps (API key)</option>
                    </select>
                    {provider === "google" && (
                        <input data-testid="gmaps-key" placeholder="Google Maps API key" value={gKey} onChange={(e) => setGKey(e.target.value)} className="inp max-w-[360px]" />
                    )}
                    <button onClick={setProviderAndKey} className="bg-primary text-primary-foreground rounded-xl px-3 py-2 text-sm font-semibold">Save</button>
                </div>
            </div>

            <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-4">
                <div className="bg-card border border-border rounded-2xl overflow-hidden" style={{ height: 480 }}>
                    <MapContainer center={DEFAULT_CENTER} zoom={11} style={{ height: "100%", width: "100%" }}>
                        <TileLayer url={tileUrl} attribution={tileAttr} />
                        {rows.filter((z) => z.status === "ACTIVE").map((z) => z.center_lat && z.center_lng && (
                            <Circle key={z.id} center={[z.center_lat, z.center_lng]} radius={(z.radius_km || 5) * 1000}
                                pathOptions={{ color: "hsl(142 71% 35%)", fillOpacity: 0.15 }} />
                        ))}
                        {rows.filter((z) => z.status !== "ACTIVE").map((z) => z.center_lat && z.center_lng && (
                            <Circle key={z.id} center={[z.center_lat, z.center_lng]} radius={(z.radius_km || 5) * 1000}
                                pathOptions={{ color: "hsl(0 84% 60%)", fillOpacity: 0.08, dashArray: "6 4" }} />
                        ))}
                    </MapContainer>
                </div>
                <div className="bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden">
                    {rows.map((z) => {
                        const on = z.status === "ACTIVE";
                        return (
                            <div key={z.id} data-testid={`zone-${z.id}`} className="p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-bold flex items-center gap-2"><MapPin size={14} /> {z.name}
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${on ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{on ? "ON" : "OFF"}</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground">Base ₹{z.base_delivery_fee} • ₹{z.per_km_fee}/km after 1km</p>
                                        <p className="text-xs text-muted-foreground">Radius {z.radius_km} km • Min order ₹{z.min_order_value || 99}{z.free_delivery_enabled && z.free_delivery_above ? <> • <span className="text-emerald-600 font-semibold">Free above ₹{z.free_delivery_above}</span></> : null}</p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button data-testid={`toggle-zone-${z.id}`} onClick={() => toggleZone(z)}
                                            title={on ? "Pause this zone" : "Activate this zone"}
                                            className={`p-2 rounded-lg ${on ? "hover:bg-emerald-50 text-emerald-600" : "hover:bg-red-50 text-red-600"}`}>
                                            <Power size={14} />
                                        </button>
                                        <button onClick={() => openEdit(z)} className="p-2 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                                        <button onClick={() => remove(z.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {rows.length === 0 && <div className="p-6 text-sm text-muted-foreground">No zones yet.</div>}
                </div>
            </div>

            {/* Offline banner editor */}
            <div className="mt-6 bg-card border border-border rounded-2xl p-4">
                <p className="font-bold text-sm">Zone-offline banner (shown on storefront when a customer&apos;s area is OFF)</p>
                <p className="text-xs text-muted-foreground mb-2">This message appears as a floating banner on the customer&apos;s home screen when their zone is paused or not yet covered.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                    <textarea data-testid="zone-offline-msg" className="inp flex-1" rows={2} value={tenantSettings.zone_offline_message}
                        onChange={(e) => setTenantSettings({ zone_offline_message: e.target.value })}
                        placeholder="We&apos;re currently not delivering to your location. Please check back soon." />
                    <button onClick={saveOfflineMsg} data-testid="save-zone-msg" className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-bold text-sm">Save message</button>
                </div>
            </div>

            {open && (
                <Modal onClose={() => setOpen(false)} title={edit ? "Edit zone" : "Create zone"}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Name"><input data-testid="zone-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field full label="Pincodes (comma)"><input className="inp" value={form.pincodes} onChange={(e) => setForm({ ...form, pincodes: e.target.value })} placeholder="500032, 500033" /></Field>
                        <Field label="Centre lat"><input className="inp" value={form.center_lat} onChange={(e) => setForm({ ...form, center_lat: e.target.value })} /></Field>
                        <Field label="Centre lng"><input className="inp" value={form.center_lng} onChange={(e) => setForm({ ...form, center_lng: e.target.value })} /></Field>
                        <Field label="Radius (km)"><input type="number" className="inp" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })} /></Field>
                        <Field label="Min order ₹"><input type="number" className="inp" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} /></Field>
                        <Field label="Base delivery ₹"><input type="number" className="inp" value={form.base_delivery_fee} onChange={(e) => setForm({ ...form, base_delivery_fee: e.target.value })} /></Field>
                        <Field label="Per km ₹"><input type="number" className="inp" value={form.per_km_fee} onChange={(e) => setForm({ ...form, per_km_fee: e.target.value })} /></Field>
                        <Field full label="Free delivery (Zone-level — overrides shop setting)">
                            <div className="flex items-center gap-3 flex-wrap mt-1">
                                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                                    <input data-testid="zone-free-delivery-enabled" type="checkbox" className="accent-primary w-4 h-4"
                                        checked={!!form.free_delivery_enabled}
                                        onChange={(e) => setForm({ ...form, free_delivery_enabled: e.target.checked })} />
                                    <span className="font-semibold">{form.free_delivery_enabled ? "ON" : "OFF"}</span>
                                </label>
                                <input data-testid="zone-free-delivery-above" type="number" className="inp max-w-[180px]"
                                    placeholder="Free above ₹ (e.g. 499)"
                                    disabled={!form.free_delivery_enabled}
                                    value={form.free_delivery_above ?? ""}
                                    onChange={(e) => setForm({ ...form, free_delivery_above: e.target.value })} />
                            </div>
                            <span className="text-[11px] text-muted-foreground mt-1">
                                When ON, customers in this zone get free delivery above ₹{form.free_delivery_above || "?"}.
                                Rider still gets paid the base + per-km fee — the platform absorbs the gap.
                                Disable to fall back to the per-shop Free Delivery setting.
                            </span>
                        </Field>
                        <Field full label="GST on Delivery (override tenant default)">
                            <div className="flex items-center gap-2 flex-wrap">
                                <select className="inp max-w-[160px]" value={form.gst_on_delivery_enabled === null ? "" : form.gst_on_delivery_enabled ? "on" : "off"}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setForm({ ...form, gst_on_delivery_enabled: v === "" ? null : v === "on" });
                                    }}>
                                    <option value="">Inherit tenant</option>
                                    <option value="on">Override ON</option>
                                    <option value="off">Override OFF</option>
                                </select>
                                {form.gst_on_delivery_enabled === true && (
                                    <input type="number" placeholder="GST %" className="inp max-w-[120px]" value={form.gst_on_delivery_percent}
                                        onChange={(e) => setForm({ ...form, gst_on_delivery_percent: e.target.value })} />
                                )}
                            </div>
                        </Field>
                        <Field full label="Payment overrides">
                            <div className="grid grid-cols-2 gap-2">
                                <PayOverride label="COD" value={form.cod_enabled} onChange={(v) => setForm({ ...form, cod_enabled: v })} testid="zone-cod" />
                                <PayOverride label="Online" value={form.online_payment_enabled} onChange={(v) => setForm({ ...form, online_payment_enabled: v })} testid="zone-online" />
                            </div>
                        </Field>
                    </div>
                    <div className="mt-3">
                        <LocationSearchBar
                            testid="zone-search"
                            placeholder="Search a city, area or landmark…"
                            onPick={([lat, lng]) => setForm((f) => ({ ...f, center_lat: lat.toFixed(6), center_lng: lng.toFixed(6) }))}
                        />
                    </div>
                    <div className="mt-3 relative bg-card border border-border rounded-2xl overflow-hidden" style={{ height: 280 }}>
                        <MapContainer center={[Number(form.center_lat), Number(form.center_lng)]} zoom={13} style={{ height: "100%", width: "100%" }}>
                            <TileLayer url={tileUrl} attribution={tileAttr} />
                            <Recenter to={[Number(form.center_lat), Number(form.center_lng)]} />
                            <ClickPicker onPick={([lat, lng]) => setForm({ ...form, center_lat: lat.toFixed(6), center_lng: lng.toFixed(6) })} />
                            <Marker position={[Number(form.center_lat), Number(form.center_lng)]} />
                            <Circle center={[Number(form.center_lat), Number(form.center_lng)]} radius={Number(form.radius_km) * 1000}
                                pathOptions={{ color: "hsl(142 71% 35%)", fillOpacity: 0.15 }} />
                        </MapContainer>
                        <button
                            type="button"
                            data-testid="zone-use-current"
                            onClick={useZoneCurrentLocation}
                            className="absolute z-[400] right-3 bottom-3 bg-card border border-border shadow-md rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5"
                            title="Use current location">
                            <LocateFixed size={14} className="text-primary" /> Current location
                        </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Tip: click anywhere on the map to set the zone centre.</p>
                    <button data-testid="zone-save" disabled={busy || !form.name} onClick={save}
                        className="mt-3 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
                    </button>
                </Modal>
            )}

            {/* iter102c — wedge-safe location gate for the "Use current location" button in the zone editor. */}
            {locGate && (
                <PermissionGateModal
                    permission="location"
                    state={locGate}
                    onClose={() => setLocGate(null)}
                    onRequest={async () => {
                        const r = await requestLocationPermission();
                        if (r.state === "granted" && r.coords) {
                            setLocGate(null);
                            applyCoords(r.coords.lat, r.coords.lng);
                        } else if (r.state === "denied") {
                            setLocGate("denied");
                        }
                    }}
                />
            )}
        </div>
    );
}


function PayOverride({ label, value, onChange, testid }) {
    const v = value === null || value === undefined ? "" : value ? "on" : "off";
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">{label}</p>
            <select data-testid={testid} className="inp" value={v}
                onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "on")}>
                <option value="">Inherit tenant</option>
                <option value="on">Override ON</option>
                <option value="off">Override OFF</option>
            </select>
        </div>
    );
}
